import React, {useCallback, useMemo} from "react";
import NiceModal from "@ebay/nice-modal-react";
import {DndContext, DragEndEvent, closestCenter} from "@dnd-kit/core";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {SortableContext, verticalListSortingStrategy} from "@dnd-kit/sortable";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {LinearProgress} from "@mui/material";
import {GridColDef} from "@mui/x-data-grid";
import {toast} from "sonner";
import {Box, Chip, Colors, Text} from "@bounce/shared/ui";
import {useGetPaymentFlows} from "../../apis/requests/payment-flows/getPaymentFlows";
import {PaymentFlow, PaymentFlowPolicies, PaymentFlowType} from "../../apis/requests/payment-flows/types";
import {useUpdatePaymentFlowsPriority} from "../../apis/requests/payment-flows/updatePaymentFlowsPriority";
import {
  DndEnabledContext,
  DragHandleCell,
  ReorderChangesBar,
  SortableGridRow,
  useRowOrder,
  useSortableSensors,
} from "../../components/sortable";
import {Button, LIST_HEADER_HEIGHT, ListDataGrid, ListPageHeader, RowActions, Tooltip} from "../../ui/components";
import DeletePaymentFlowDialog, {DeletePaymentFlowCopy} from "./DeletePaymentFlowDialog";
import {getVariantRows, isDefaultFlow, matchesAllTransactions, moveWithinPinned} from "./helpers";

const DEFAULT_FLOW_TOOLTIP = "Default test. Always runs last and can't be moved.";
const MATCHES_ALL_LABEL = "Matches all transactions";
const WEIGHT_BAR_WIDTH = 70;
const WEIGHT_BAR_HEIGHT = 6;
const WEIGHT_TEXT_WIDTH = 36;
const VARIANT_LABEL_WIDTH = 104;
const PRIORITY_BADGE_SIZE = 28;
const PRIORITY_BADGE_RADIUS = "8px";
const SAVE_SUCCESS_TEXT = "Priorities updated";
const DRAG_ACTIVATION_DISTANCE = 8;
const DRAG_COLUMN_WIDTH = 48;
const ACTIONS_COLUMN_WIDTH = 72;
const ROW_ICON_SIZE = 20;

const DefaultFlowLock = () => (
  <Tooltip title={DEFAULT_FLOW_TOOLTIP} placement="right">
    <Box sx={{display: "flex", alignItems: "center", color: Colors.grey._400}}>
      <LockOutlinedIcon sx={{fontSize: ROW_ICON_SIZE}} />
    </Box>
  </Tooltip>
);

const PriorityBadge = ({position}: {position: number}) => (
  <Box
    sx={{
      width: PRIORITY_BADGE_SIZE,
      height: PRIORITY_BADGE_SIZE,
      borderRadius: PRIORITY_BADGE_RADIUS,
      backgroundColor: Colors.tertiary._100,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Text size={12} variant="inter600" sx={{color: Colors.grey._700}}>
      {position}
    </Text>
  </Box>
);

const NameCell = ({flow}: {flow: PaymentFlow}) => (
  <Text
    size={14}
    variant="inter600"
    sx={{
      color: Colors.grey._900,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
      py: 1,
    }}
  >
    {flow.name}
  </Text>
);

const ConditionsCell = ({flow}: {flow: PaymentFlow}) => (
  <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, py: 1}}>
    {matchesAllTransactions(flow) ? (
      <Chip size_="small" variant_="inherit" label={MATCHES_ALL_LABEL} />
    ) : (
      flow.conditions.rules.map((group, index) => (
        <Chip key={group.name ?? index} size_="small" variant_="inherit" label={group.name || `Group ${index + 1}`} />
      ))
    )}
  </Box>
);

type RenderPolicy<T extends PaymentFlowType> = (policy: PaymentFlowPolicies[T]) => React.ReactNode;

const VariantsCell = <T extends PaymentFlowType>({
  flow,
  renderPolicy,
}: {
  flow: PaymentFlow<T>;
  renderPolicy: RenderPolicy<T>;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "max-content minmax(0, 1fr)",
      alignItems: "baseline",
      columnGap: 3,
      rowGap: 1.25,
      py: 1,
    }}
  >
    {getVariantRows(flow).map((variant, index) => (
      <React.Fragment key={variant.key}>
        {index > 0 && <Box sx={{gridColumn: "1 / -1", height: "1px", backgroundColor: Colors.tertiary._300}} />}
        <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
          <Chip size_="small" variant_="blue" label={variant.letter} />
          <Text size={13} variant="inter600" sx={{width: WEIGHT_TEXT_WIDTH}}>
            {`${variant.weight}%`}
          </Text>
          <LinearProgress
            variant="determinate"
            value={variant.weight}
            sx={{
              width: WEIGHT_BAR_WIDTH,
              height: WEIGHT_BAR_HEIGHT,
              borderRadius: WEIGHT_BAR_HEIGHT / 2,
              backgroundColor: Colors.blue._100,
              "& .MuiLinearProgress-bar": {backgroundColor: Colors.blue._800},
            }}
          />
          <Text size={13} variant="inter600" sx={{width: VARIANT_LABEL_WIDTH}}>
            {variant.label}
          </Text>
        </Box>
        {renderPolicy(variant.policy)}
      </React.Fragment>
    ))}
  </Box>
);

type FlowEditor = {
  addLabel: string;
  open: (flowId?: number) => void;
};

type Props<T extends PaymentFlowType> = {
  type: T;
  title: string;
  noRowsText: string;
  loadErrorText: string;
  deleteCopy: DeletePaymentFlowCopy;
  renderPolicy: RenderPolicy<T>;
  editor?: FlowEditor;
};

const PaymentFlowsList = <T extends PaymentFlowType>({
  type,
  title,
  noRowsText,
  loadErrorText,
  deleteCopy,
  renderPolicy,
  editor,
}: Props<T>) => {
  const {data, isLoading, error} = useGetPaymentFlows({type});
  const {mutate: updatePaymentFlowsPriority, isPending: isReordering} = useUpdatePaymentFlowsPriority();

  const sensors = useSortableSensors(DRAG_ACTIVATION_DISTANCE);

  const flowById = useMemo(() => new Map((data ?? []).map((flow) => [flow.id, flow])), [data]);
  const baselineIds = useMemo(() => (data ?? []).map((flow) => flow.id), [data]);
  const pinnedIds = useMemo(() => new Set((data ?? []).filter(isDefaultFlow).map((flow) => flow.id)), [data]);

  const {orderedIds, hasChanges: hasReorderChanges, reorder, discard} = useRowOrder(baselineIds);

  const orderedFlows = useMemo(
    () => orderedIds.map((id) => flowById.get(id)).filter((flow): flow is PaymentFlow<T> => Boolean(flow)),
    [orderedIds, flowById]
  );
  const rankById = useMemo(() => new Map(orderedIds.map((id, index) => [id, index + 1])), [orderedIds]);
  const draggableIds = useMemo(() => orderedIds.filter((id) => !pinnedIds.has(id)), [orderedIds, pinnedIds]);

  const handleDragEnd = useCallback(
    ({active, over}: DragEndEvent) => {
      if (!over || active.id === over.id) return;
      reorder(moveWithinPinned(orderedIds, pinnedIds, Number(active.id), Number(over.id)));
    },
    [orderedIds, pinnedIds, reorder]
  );

  const handleSaveOrder = () => {
    updatePaymentFlowsPriority(
      {payment_flow_ids: orderedIds.filter((id) => !pinnedIds.has(id))},
      {
        onSuccess: () => {
          toast.success(SAVE_SUCCESS_TEXT);
          discard();
        },
      }
    );
  };

  const columns = useMemo<GridColDef<PaymentFlow<T>>[]>(
    () => [
      {
        field: "__drag__",
        headerName: "",
        width: DRAG_COLUMN_WIDTH,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({row}) => (pinnedIds.has(row.id) ? <DefaultFlowLock /> : <DragHandleCell />),
      },
      {
        field: "priority",
        headerName: "#",
        width: 70,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({row}) => <PriorityBadge position={rankById.get(row.id) ?? 0} />,
      },
      {
        field: "name",
        headerName: "Test name",
        width: 220,
        sortable: false,
        renderCell: ({row}) => <NameCell flow={row} />,
      },
      {
        field: "conditions",
        headerName: "Conditions",
        width: 220,
        sortable: false,
        renderCell: ({row}) => <ConditionsCell flow={row} />,
      },
      {
        field: "config",
        headerName: "Variants",
        flex: 1,
        sortable: false,
        renderCell: ({row}) => <VariantsCell flow={row} renderPolicy={renderPolicy} />,
      },
      {
        field: "actions",
        headerName: "",
        width: ACTIONS_COLUMN_WIDTH,
        sortable: false,
        renderCell: ({row}) => (
          <RowActions
            onEdit={editor && (() => editor.open(row.id))}
            onDelete={
              pinnedIds.has(row.id)
                ? undefined
                : () => NiceModal.show(DeletePaymentFlowDialog, {...deleteCopy, flowId: row.id, flowName: row.name})
            }
          />
        ),
      },
    ],
    [pinnedIds, rankById, renderPolicy, editor, deleteCopy]
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title={title}>
        {editor && (
          <Button buttonType="contained-primary" size="small" onClick={() => editor.open()}>
            {editor.addLabel}
          </Button>
        )}
      </ListPageHeader>
      {hasReorderChanges && <ReorderChangesBar onSave={handleSaveOrder} onDiscard={discard} disabled={isReordering} />}
      {error ? (
        <Text size={14}>{loadErrorText}</Text>
      ) : (
        <DndEnabledContext.Provider value={!isReordering}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={draggableIds} strategy={verticalListSortingStrategy}>
              <ListDataGrid
                rows={orderedFlows}
                columns={columns}
                loading={isLoading}
                getRowId={(row) => row.id}
                onRowClick={editor && (({row}) => editor.open(row.id))}
                pageSizeOptions={[10, 25, 50, 100]}
                columnHeaderHeight={LIST_HEADER_HEIGHT}
                slots={{row: SortableGridRow}}
                noRowsText={noRowsText}
              />
            </SortableContext>
          </DndContext>
        </DndEnabledContext.Provider>
      )}
    </Box>
  );
};

export default PaymentFlowsList;
